import { useRef, useState } from 'react';

import { Auth } from '@aws-amplify/auth';

import { ConstValues, convertToEmailWithPrefix } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { ValueOf } from 'lib/core/types';

import resetRelatedCallsInStorage from 'lib/common/contexts/RelatedCallsContext/utils';

import { LogEvents, logger } from 'lib/common/components/LoggerController';

import SIGN_OUT_TYPES from '../constants/signOutTypes';
import { AuthStages, AuthState } from '../types/AuthState';
import handleConnectSignOut from '../utils/handleConnectSignOut';
import shouldRefreshToken from '../utils/shouldRefreshToken';
import { useHandleAuthStep } from './useHandleAuthStep';
import { useMfaFlow } from './useMfaFlow';

type Fetch = (url: string, options?: RequestInit) => Promise<Response>;

export const useAuthFlow = ({
  setError,
  fetch_,
  email,
  setSignedOut,
  isolatedModeDetails,
}: {
  setError: (error: boolean) => void;
  fetch_: Fetch;
  email: string;
  setSignedOut: (signedOut: boolean) => void;
  isolatedModeDetails?: { token: string; connectUsername: string };
}) => {
  const { config } = useConfigContext();

  const [authState, setAuthState] = useState<AuthState>({ stage: AuthStages.initial });
  const [invalidCode, setInvalidCode] = useState(false);
  const [signingOut, setSigningOut] = useState<boolean>(false);

  const signingOutRef = useRef<null | ValueOf<typeof SIGN_OUT_TYPES>>(null);

  const { selectMfaDestination, mfaDestinations, setMfaDestinations, provideMfaCode, mfaSelection } = useMfaFlow({
    setInvalidCode,
    setAuthState,
    setError,
    authState,
  });

  const { handleAuthStep } = useHandleAuthStep({
    setMfaDestinations,
    setInvalidCode,
    setError,
    isolatedModeDetails,
  });

  const beginAuth = async (existingConnectUsername?: string) => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();

      const userMatchesStorage =
        existingConnectUsername &&
        currentUser.attributes.email !==
          convertToEmailWithPrefix({ username: existingConnectUsername, tenantId: config.TENANT_ID });

      if (userMatchesStorage) {
        throw new Error('User is not the same as the one logged in');
      }

      if (currentUser && !shouldRefreshToken()) {
        handleAuthStep({ stage: AuthStages.complete_auto });
        setAuthState({ stage: AuthStages.complete_auto });
        return;
      }
    } catch {}

    const newState = await handleAuthStep({ stage: AuthStages.initial });
    setAuthState(newState);
  };

  const signIn = async (existingConnectUsername?: string) => {
    if (authState.stage.startsWith('complete')) {
      return;
    }

    try {
      await beginAuth(existingConnectUsername);

      logger.info(LogEvents.AUTH.SIGN_IN.SUCCESS, { username: '' });
    } catch (error) {
      logger.info(LogEvents.AUTH.SIGN_IN.FAIL, { username: '', error });
      setError(true);
    }
  };

  const signOut = async (type: ConstValues<typeof SIGN_OUT_TYPES> = SIGN_OUT_TYPES.MANUAL_SIGN_OUT) => {
    if (signingOutRef?.current) {
      return;
    }

    logger.info(LogEvents.AUTH.SIGN_OUT.INITIATED, { user: email });

    signingOutRef.current = type;
    setSigningOut(true);

    // Clear related calls values in local
    // storage to prevent displaying checkbox
    // after agent logout
    resetRelatedCallsInStorage();

    handleConnectSignOut({ config });

    const body = JSON.stringify({ id: sessionStorage.getItem('user_id') });

    fetch(`${config.AGENT_SERVICE_URL}/agent/cleanup/`, {
      method: 'POST',
      body,
    });

    logger.info(LogEvents.AUTH.SIGN_OUT.COMPLETED.SUCCESS, { user: email });

    setSigningOut(false);

    setSignedOut(true);
  };

  //Cheating going to select screen as state changing isn't working as expected
  const resendCode = async () => {
    if (authState.stage !== AuthStages.mfa_entry) {
      return;
    }
    setInvalidCode(false);
    const mfaSelection = authState.mfaSelection;
    const newState = await handleAuthStep({ stage: AuthStages.initial });

    await selectMfaDestination(mfaSelection, newState);
  };

  //Cheating going to select screen as state changing isn't working as expected
  const restartAuthFlow = async () => {
    setInvalidCode(false);
    const newState = await handleAuthStep({ stage: AuthStages.initial });
    setAuthState(newState);
  };

  const clearInvalidCode = () => setInvalidCode(false);

  return {
    signIn,
    signOut,
    signingOutRef,
    signingOut,
    invalidCode,
    authStage: authState.stage,

    // MFA related auth flow
    selectMfaDestination,
    provideMfaCode,
    mfaDestinations,
    mfaSelection,
    resendCode,
    restartAuthFlow,
    clearInvalidCode,
  };
};
